import { makeAuthenticatedRequest } from './api';

const BASE_PATH = '/knowledge';

const knowledgeApi = {
  // 1. Knowledge Query
  query: {
    getLearning: async (learningId, options = {}) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_query`, {
        operation: 'get_learning',
        learning_id: learningId,
        ...options
      });
    },
    search: async (filters = {}, pagination = { page: 1, page_size: 20 }) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_query`, {
        operation: 'search',
        ...filters,
        ...pagination
      });
    },
    searchByText: async (searchText, agentId, maxResults = 20) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_query`, {
        operation: 'search_by_text',
        search_text: searchText,
        agent_id: agentId,
        max_results: maxResults
      });
    },
    hybridSearch: async (searchText, embedding, agentId, maxResults = 20) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_query`, {
        operation: 'hybrid_search',
        search_text: searchText,
        embedding: embedding, // Optional if backend handles embedding generation
        agent_id: agentId,
        max_results: maxResults
      });
    },
    getRelated: async (learningId, maxResults = 10) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_query`, {
        operation: 'related',
        learning_id: learningId,
        max_results: maxResults
      });
    }
  },

  // 2. Cluster Query
  clusters: {
    list: async (agentId, filters = {}) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/cluster_query`, {
        operation: 'list_clusters',
        agent_id: agentId,
        ...filters
      });
    },
    get: async (clusterId) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/cluster_query`, {
        operation: 'get_cluster',
        cluster_id: clusterId,
        include_members: true
      });
    },
    getStats: async (agentId) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/cluster_query`, {
        operation: 'get_stats',
        agent_id: agentId
      });
    }
  },

  // 3. Quality Metrics
  quality: {
    getMetrics: async (learningId) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/quality_metrics`, {
        operation: 'get_metrics',
        learning_id: learningId
      });
    },
    getDistribution: async (agentId) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/quality_metrics`, {
        operation: 'get_distribution',
        agent_id: agentId
      });
    }
  },

  // 4. Conflict Management
  conflicts: {
    dashboard: async (params = {}) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'dashboard',
        parameters: params
      });
      return knowledgeApi._parseConflictResponse(response);
    },
    
    getConflict: async (conflictId) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'get_conflict',
        parameters: { conflict_id: conflictId }
      });
      return knowledgeApi._parseConflictResponse(response);
    },
    
    history: async (params = {}) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'history',
        parameters: params
      });
      return knowledgeApi._parseConflictResponse(response);
    },
    
    resolve: async (resolutionData) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'resolve',
        ...resolutionData
      });
      return knowledgeApi._parseConflictResponse(response);
    },
    
    bothKeep: async (data) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'both_keep',
        parameters: data
      });
      return knowledgeApi._parseConflictResponse(response);
    },
    
    dismiss: async (data) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'dismiss',
        parameters: data
      });
      return knowledgeApi._parseConflictResponse(response);
    },
    
    escalate: async (data) => {
      const response = await makeAuthenticatedRequest('POST', `${BASE_PATH}/conflict_management`, {
        action: 'escalate',
        parameters: data
      });
      return knowledgeApi._parseConflictResponse(response);
    }
  },

  // 5. Source Management
  sources: {
    list: async (filters = {}) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/source_management`, {
        operation: 'list_sources',
        ...filters
      });
    }
  },

  // 6. Pattern Management
  patterns: {
    list: async (filters = {}) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/pattern_management`, {
        operation: 'list_patterns',
        ...filters
      });
    }
  },

  // 7. Analytics Views
  analytics: {
    getDashboard: async (agentId, days = 30) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/analytics_views`, {
        operation: 'dashboard',
        agent_id: agentId,
        time_period_days: days
      });
    },
    getTrends: async (agentId, days = 90, groupBy = 'week') => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/analytics_views`, {
        operation: 'trends',
        agent_id: agentId,
        time_period_days: days,
        group_by: groupBy
      });
    }
  },

  // 8. Knowledge Write
  write: {
    updateLearning: async (learningId, updates, reviewedBy) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_write`, {
        operation: 'update_learning',
        learning_id: learningId,
        updates: updates,
        reviewed_by: reviewedBy
      });
    },
    archiveLearning: async (learningId, reason, reviewedBy) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_write`, {
        operation: 'archive_learning',
        learning_id: learningId,
        reason: reason,
        reviewed_by: reviewedBy
      });
    },
    createLearning: async (learningData) => {
        // Assuming there is a create op or using update for upsert based on backend logic
        // For now using update structure as placeholder or standard write op
        return await makeAuthenticatedRequest('POST', `${BASE_PATH}/knowledge_write`, {
            operation: 'create_learning',
            ...learningData
        });
    }
  },

  // 9. Maintenance
  maintenance: {
    triggerConsolidation: async (agentId, dryRun = false) => {
      return await makeAuthenticatedRequest('POST', `${BASE_PATH}/maintenance_operations`, {
        operation: 'trigger_consolidation',
        agent_id: agentId,
        dry_run: dryRun
      });
    }
  },

  // Internal helper to parse conflict management responses
  _parseConflictResponse: (response) => {
    // Handle different response formats
    let data = response;
    
    // If response has a data property, use that
    if (response && response.data !== undefined) {
      data = response.data;
    }
    
    // If data has a body property, parse it
    if (data && typeof data.body === 'string') {
      try {
        data = JSON.parse(data.body);
      } catch (e) {
        console.error('Failed to parse conflict response:', e);
        throw new Error('Failed to parse API response');
      }
    } else if (data && typeof data.body === 'object' && data.body !== null) {
      data = data.body;
    }
    
    // Check for errors
    if (data && data.statusCode && data.statusCode >= 400) {
      throw new Error(data.error || data.message || `API returned ${data.statusCode}`);
    }
    
    // Return the parsed data
    return data;
  }
};

export default knowledgeApi;